import { useEffect, useRef } from 'react';
import _get from 'lodash/get';
import { useQuery } from '@apollo/client';
import queries from '@nc-aftermarket/config/queryDefs';
import useSaleTableState from '../lib/useSaleTableState';
import Alert from './Alert';
import Meta from './Meta';
import SaleTable from './SaleTable';
import SaleTableActions from './SaleTableActions';
import SaleTablePagination from './SaleTablePagination';
import SaleTableRows from './SaleTableRows';
import SaleTableToolbar from './SaleTableToolbar';
import getSalePresenter from '../lib/getSalePresenter';

const defaultTableState = {
  manualPagination: true,
  pageSize: 25,
};

export default function SaleTablePage({
  fetchPolicy = 'cache-and-network',
  query,
  queryVars,
  pollInterval,
  salesPath = 'sales',
  showAddToCart = false,
  showWatch = true,
  title,
  visibleColumns,
}) {
  const scrollParent = useRef();
  const state = useSaleTableState(defaultTableState);
  const { data, error, loading, previousData } = useQuery(query, {
    pollInterval,
    fetchPolicy,
    variables: {
      ...state.api,
      ...queryVars,
      filter: {
        ...(queryVars?.filter ?? {}),
        ...state.api.filter,
        // NOTE: Reducing TLD to a string fixes an issue where data wasn't updating. Maybe try using useLazyQuery instead?
        tld:
          state.api.filter?.tld?.length > 0
            ? state.api.filter.tld.join(',')
            : undefined,
      },
    },
  });
  const resolvedSales = _get(data || previousData, salesPath, []);
  const currentPage = _get(data, `${salesPath}.page`, []);
  const previousPage = _get(previousData, `${salesPath}.page`, []);

  const endingSoon =
    resolvedSales.items
      ?.map(sale => getSalePresenter(sale))
      .filter(presenter => presenter.isEndingSoon) ?? [];

  useQuery(queries.SaleTablePageRealtimeSync, {
    pollInterval: 1000 * 5,
    skip: queryVars?.saleType !== 'auction' || endingSoon.length === 0,
    variables: {
      ids: endingSoon.map(sale => sale.id),
    },
  });

  useEffect(() => {
    if (Number.isInteger(currentPage) && currentPage !== previousPage) {
      if (!scrollParent?.current) return;
      if (window.scrollY > scrollParent.current.offsetTop) {
        scrollParent.current.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }
  }, [currentPage, previousPage]);

  if (!loading && (!data || (data && !_get(data, salesPath)))) {
    return <Alert className="tw-m-4" severity="error" title="Page not found" />;
  }

  return (
    <>
      {data?.collection && (
        <Meta
          description={data.collection.subheading}
          robots="all"
          title={`${data.collection.heading} - Namecheap`}
        />
      )}
      {error ? (
        <Alert
          className="tw-wrap tw-mt-4 tw-mx-4"
          error={error}
          severity="error"
        />
      ) : null}
      <div className="tw-wrap">
        <h1 className="tw-text-2xl md:tw-text-3xl tw-text-gray-700 tw-font-bold tw-pt-4">
          {data?.collection?.heading ?? title}
        </h1>
      </div>
      <SaleTable
        defaultState={defaultTableState}
        initialState={state}
        loading={loading}
        manualPagination
        pageCount={resolvedSales.pageCount}
        sales={resolvedSales.items}
        total={resolvedSales.total}
        visibleColumns={
          typeof visibleColumns === 'function'
            ? visibleColumns(data)
            : visibleColumns
        }>
        <div className="tw-wrap">
          <SaleTableToolbar />
        </div>
        <div className="md:tw-wrap" ref={scrollParent}>
          <SaleTableActions
            className="tw-border-t tw-border-gray-100"
            showPagination
            showAddToCart={
              typeof showAddToCart === 'function'
                ? showAddToCart(data)
                : showAddToCart
            }
            showWatch={
              typeof showWatch === 'function' ? showWatch(data) : showWatch
            }
          />
          <div className="md:tw-paper tw-overflow-hidden">
            <SaleTableRows
              showWatch={
                typeof showWatch === 'function' ? showWatch(data) : showWatch
              }
            />
          </div>
        </div>
        <div className="tw-flex tw-items-center tw-justify-center tw-py-5 tw-text-sm">
          <SaleTablePagination />
        </div>
        <p className="tw-hidden xl:tw-block tw-text-sm tw-text-center tw-pb-5">
          Tip: to sort by multiple columns, hold down <kbd>shift</kbd> while
          selecting.
        </p>
      </SaleTable>
    </>
  );
}
